/**
 * Data access for every screen. Reads and writes go to Supabase (under RLS) or the
 * API Worker's /admin routes. In DEV preview mode (see ./preview.ts) the same calls
 * are answered from local fixtures instead; that branch is removed from production builds.
 */
import { adminBlob, adminFetch } from "./api";
import { addDays, localMidnight, localYmd } from "./format";
import { PREVIEW } from "./preview";
import {
  supabase,
  type Booking,
  type CrewMember,
  type Lead,
  type OutboxHealth,
  type Profile,
  type Quote,
  type RateCard,
} from "./supabase";

const devFixtures = async () => {
  const fixtures = await import("../dev/fixtures");
  await fixtures.ready();
  return fixtures;
};

const CREW_COLUMNS =
  "id, full_name, phone, email, profile_id, notes, active, tickets_held, driver_licence_class, last_worked_month, is_partner, public_liability_expiry";

/* ---------- Profile ---------- */

export async function getProfile(userId: string): Promise<Profile | null> {
  if (PREVIEW) return (await devFixtures()).profile;
  const key = `ops-portal:profile:${userId}`;
  const { data, error } = await supabase.from("profiles").select("id, full_name, email, role, organisation_id").eq("id", userId).maybeSingle();
  if (error) {
    // No signal: the profile saved last time, so the right app (crew or office) still opens.
    try {
      const saved = window.localStorage.getItem(key);
      if (saved) return JSON.parse(saved) as Profile;
    } catch {
      /* storage blocked */
    }
    throw error;
  }
  try {
    if (data) window.localStorage.setItem(key, JSON.stringify(data));
  } catch {
    /* storage blocked */
  }
  return data as Profile | null;
}

/* ---------- Leads ---------- */

export async function listLeads(): Promise<Lead[]> {
  if (PREVIEW) return (await devFixtures()).leads();
  const { data, error } = await supabase.from("leads").select("*").order("created_at", { ascending: false }).limit(200);
  if (error) throw error;
  return data as Lead[];
}

export async function setLeadStatus(id: string, status: string): Promise<void> {
  if (PREVIEW) return (await devFixtures()).setLeadStatus(id, status);
  const { error } = await supabase.from("leads").update({ status }).eq("id", id);
  if (error) throw error;
}

/** Leads still in `new`, newest first, with the total count for the badge and KPI. */
export async function newLeads(limit = 5): Promise<{ count: number; items: Lead[] }> {
  if (PREVIEW) {
    const items = (await devFixtures()).leads().filter((l) => l.status === "new");
    return { count: items.length, items: items.slice(0, limit) };
  }
  const { data, error, count } = await supabase
    .from("leads")
    .select("*", { count: "exact" })
    .eq("status", "new")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return { count: count ?? data.length, items: data as Lead[] };
}

/* ---------- Quotes ---------- */

export async function listQuotes(): Promise<Quote[]> {
  if (PREVIEW) return (await devFixtures()).quotes();
  const { data, error } = await supabase
    .from("quotes")
    .select("id, quote_number, status, total, valid_until, created_at, pdf_path, job_title, client_name")
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw error;
  return data as Quote[];
}

/**
 * Generate (or regenerate) the quote PDF, then return it. The API retries a busy or slow
 * browser once, so allow up to 75 s before giving up with a clear message.
 */
export async function generateQuotePdf(id: string): Promise<Blob> {
  if (PREVIEW) return previewPdf();
  await adminFetch(`/quotes/${id}/pdf`, { method: "POST" }, { timeoutMs: 75_000 });
  return adminBlob(`/quotes/${id}/pdf`);
}

/** The stored quote PDF. */
export async function fetchQuotePdf(id: string): Promise<Blob> {
  if (PREVIEW) return previewPdf();
  return adminBlob(`/quotes/${id}/pdf`);
}

function previewPdf(): Promise<Blob> {
  return new Promise((resolve) => setTimeout(() => resolve(new Blob(["Preview mode: no PDF is generated."], { type: "text/plain" })), 600));
}

/* ---------- Bookings ---------- */

/** Bookings from `fromIso` onwards, with their crew allocations. */
export async function listBookings(fromIso: string): Promise<Booking[]> {
  if (PREVIEW) return (await devFixtures()).bookings().filter((b) => b.starts_at >= fromIso);
  const { data, error } = await supabase
    .from("bookings")
    .select("*, allocations(crew_member_id)")
    .gte("starts_at", fromIso)
    .order("starts_at")
    .limit(500);
  if (error) throw error;
  return data as Booking[];
}

/** Bookings starting on the Sydney calendar day `ymd` (default today). */
export async function bookingsOnDay(ymd = localYmd()): Promise<Booking[]> {
  const from = localMidnight(ymd).toISOString();
  const to = localMidnight(addDays(ymd, 1)).toISOString();
  if (PREVIEW) return (await devFixtures()).bookings().filter((b) => b.starts_at >= from && b.starts_at < to);
  const { data, error } = await supabase
    .from("bookings")
    .select("*, allocations(crew_member_id)")
    .gte("starts_at", from)
    .lt("starts_at", to)
    .order("starts_at");
  if (error) throw error;
  return data as Booking[];
}

export async function setBookingStatus(id: string, status: string): Promise<void> {
  if (PREVIEW) return (await devFixtures()).setBookingStatus(id, status);
  const { error } = await supabase.from("bookings").update({ status }).eq("id", id);
  if (error) throw error;
}

/* ---------- Crew ---------- */

export async function listCrew(): Promise<CrewMember[]> {
  if (PREVIEW) return (await devFixtures()).crew();
  const { data, error } = await supabase.from("crew_members").select(CREW_COLUMNS).order("full_name");
  if (error) throw error;
  return data as CrewMember[];
}

/* ---------- Rate card ---------- */

/** The rate card in effect today (Sydney), with crew rates and service prices. */
export async function currentRateCard(): Promise<RateCard | null> {
  if (PREVIEW) return (await devFixtures()).rateCard;
  const today = localYmd();
  const { data, error } = await supabase
    .from("rate_cards")
    .select("id, name, effective_from, effective_to, gst_rate, notes, rate_card_rows(*), service_prices(*)")
    .lte("effective_from", today)
    .or(`effective_to.is.null,effective_to.gte.${today}`)
    .order("effective_from", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const card = data as unknown as RateCard;
  card.rate_card_rows = [...card.rate_card_rows].sort((a, b) => a.crew_size - b.crew_size);
  card.service_prices = [...card.service_prices].sort((a, b) => a.code.localeCompare(b.code));
  return card;
}

/* ---------- CRM delivery queue (API Worker) ---------- */

export async function crmOutboxProblems(): Promise<OutboxHealth> {
  if (PREVIEW) return (await devFixtures()).outbox;
  const [dead, failed] = await Promise.all([
    adminFetch<OutboxHealth>("/crm/outbox?status=dead&limit=20"),
    adminFetch<OutboxHealth>("/crm/outbox?status=failed&limit=20"),
  ]);
  return { ...dead, items: [...dead.items, ...failed.items] };
}

export async function retryOutboxItem(id: string): Promise<void> {
  if (PREVIEW) return;
  await adminFetch(`/crm/outbox/${id}/retry`, { method: "POST" });
}

/* ---------- Xero ---------- */

export interface XeroStatus {
  configured: boolean;
  connected: boolean;
  status?: "active" | "expired" | null;
  tenantName?: string | null;
  connectedAt?: string | null;
  lastError?: string | null;
}

export interface QuoteInvoice {
  entity_id: string;
  remote_id: string;
  remote_number: string | null;
  remote_status: string | null;
}

export const xeroInvoiceUrl = (invoiceId: string) => `https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${encodeURIComponent(invoiceId)}`;

export async function xeroStatus(): Promise<XeroStatus> {
  if (PREVIEW) return { configured: true, connected: true, status: "active", tenantName: "Preview Pty Ltd", connectedAt: new Date().toISOString() };
  return adminFetch<XeroStatus>("/integrations/xero");
}

/** Start the Xero consent flow: returns the Xero sign-in URL to send the browser to. */
export async function startXeroConnect(): Promise<string> {
  if (PREVIEW) throw new Error("Connecting Xero is not available in preview mode.");
  return (await adminFetch<{ url: string }>("/integrations/xero/connect", { method: "POST" })).url;
}

export async function disconnectXero(): Promise<void> {
  if (PREVIEW) return;
  await adminFetch("/integrations/xero/disconnect", { method: "POST" });
}

/** Xero invoices already raised for quotes, keyed by quote id. */
export async function listQuoteInvoices(): Promise<Record<string, QuoteInvoice>> {
  if (PREVIEW) return {};
  const { data, error } = await supabase
    .from("accounting_links")
    .select("entity_id, remote_id, remote_number, remote_status")
    .eq("provider", "xero")
    .eq("entity_type", "quote")
    .eq("remote_type", "invoice");
  if (error) throw error;
  return Object.fromEntries((data as QuoteInvoice[]).map((r) => [r.entity_id, r]));
}

export async function sendQuoteToXero(id: string): Promise<{ invoice: { remoteId: string; remoteNumber: string | null; url: string }; existing: boolean; warnings: string[] }> {
  if (PREVIEW) return { invoice: { remoteId: "preview", remoteNumber: "INV-PREVIEW", url: "#" }, existing: false, warnings: [] };
  return adminFetch(`/integrations/xero/quotes/${id}/invoice`, { method: "POST" }, { timeoutMs: 45_000 });
}
